import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
    Plus, Pencil, Trash2, Crown, Search, ChevronDown, ChevronRight, UserPlus, Copy, Check, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasAnyRole } from "@/context/AuthContext";
import { ACTIVATION_MESSAGE_MASTER, ACTIVATION_URL } from "@/lib/activationMessage";

const ORDEN_GRUPOS = ["1", "2", "3", "4", "Coworking"];
const labelGrupo = (g) => (g && /^\d+$/.test(g) ? `Piso ${g}` : (g || "Sin grupo"));
const labelGrupoCorto = (g) => (g && /^\d+$/.test(g) ? g : (g || "—"));

const emptyUni = { etiqueta: "", piso: "1", capacidad: "" };
const emptyMaster = { nombre: "", apellido: "", email: "", whatsapp: "" };

export default function UnidadesMasters() {
    const { user } = useAuth();
    const isOper = hasAnyRole(user, "operaciones");
    const isRecepOnly = hasAnyRole(user, "recepcion") && !isOper;

    const [unidades, setUnidades] = useState([]);
    const [copiado, setCopiado] = useState(false);
    const [usuarios, setUsuarios] = useState([]);
    const [reglas, setReglas] = useState([]);

    const [query, setQuery] = useState("");
    const [filtroGrupo, setFiltroGrupo] = useState("todos");
    const [expanded, setExpanded] = useState(() => new Set(["1"]));

    const [uniOpen, setUniOpen] = useState(false);
    const [uniForm, setUniForm] = useState(emptyUni);
    const [uniEdit, setUniEdit] = useState(null);

    const [masterUnidad, setMasterUnidad] = useState(null);
    const [masterForm, setMasterForm] = useState(emptyMaster);
    const [showMasterForm, setShowMasterForm] = useState(false);

    const masterFormValido = !!masterForm.nombre.trim()
        && !!masterForm.apellido.trim()
        && /\S+@\S+\.\S+/.test(masterForm.email.trim());

    const [autosUnidad, setAutosUnidad] = useState(null);

    // Reset de contraseña
    const [resetFor, setResetFor] = useState(null);
    const [resetCopied, setResetCopied] = useState(false);

    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [a, b, c] = await Promise.all([
            api.get("/unidades"),
            api.get("/usuarios-edificio"),
            api.get("/reglas-uso-salas"),
        ]);
        setUnidades(a.data); setUsuarios(b.data); setReglas(c.data);
    };
    useEffect(() => { load(); }, []);

    const matchRule = (cap) =>
        cap ? reglas.find((r) => r.capacidad_desde <= cap && cap <= r.capacidad_hasta) : null;
    const usuariosDe = (uid) => usuarios.filter((u) => u.unidad_id === uid);
    const masterDe = (uid) => usuariosDe(uid).find((u) => u.es_master);
    const autoDe = (uid) => usuariosDe(uid).filter((u) => !u.es_master);

    // ---------- Unidades CRUD ----------
    const newUni = () => { setUniEdit(null); setUniForm(emptyUni); setUniOpen(true); };
    const editUni = (u) => {
        setUniEdit(u.unidad_id);
        setUniForm({ etiqueta: u.etiqueta, piso: u.piso || "1", capacidad: u.capacidad ?? "" });
        setUniOpen(true);
    };
    const submitUni = async () => {
        try {
            const payload = {
                etiqueta: uniForm.etiqueta.trim(),
                piso: uniForm.piso || null,
                capacidad: uniForm.capacidad === "" ? null : parseInt(uniForm.capacidad, 10),
            };
            if (!payload.etiqueta) return toast.error("Ingresá una etiqueta");
            if (uniEdit) await api.patch(`/unidades/${uniEdit}`, payload);
            else await api.post("/unidades", payload);
            toast.success("Unidad guardada"); setUniOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const delUni = async (u) => {
        if (!window.confirm(`¿Eliminar ${u.etiqueta}?`)) return;
        try { await api.delete(`/unidades/${u.unidad_id}`); toast.success("Eliminada"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    // ---------- Filtros / agrupación ----------
    const gruposVisibles = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = unidades.filter((u) => {
            if (filtroGrupo !== "todos" && (u.piso || "") !== filtroGrupo) return false;
            if (!q) return true;
            if (u.etiqueta.toLowerCase().includes(q)) return true;
            return usuariosDe(u.unidad_id).some(
                (x) => x.nombre.toLowerCase().includes(q) || x.email.toLowerCase().includes(q)
            );
        });
        const map = new Map();
        for (const u of filtered) {
            const g = u.piso || "Sin grupo";
            if (!map.has(g)) map.set(g, []);
            map.get(g).push(u);
        }
        const sorted = [...map.entries()].sort(([a], [b]) => {
            const ia = ORDEN_GRUPOS.indexOf(a);
            const ib = ORDEN_GRUPOS.indexOf(b);
            const ka = ia === -1 ? 999 : ia;
            const kb = ib === -1 ? 999 : ib;
            if (ka !== kb) return ka - kb;
            return a.localeCompare(b);
        });
        return sorted.map(([grupo, items]) => ({
            grupo,
            items: items.sort((x, y) => x.etiqueta.localeCompare(y.etiqueta, "es", { numeric: true })),
        }));
    }, [unidades, usuarios, query, filtroGrupo]);

    useEffect(() => {
        if (query.trim()) setExpanded(new Set(gruposVisibles.map((g) => g.grupo)));
    }, [query, gruposVisibles]);

    const toggleGrupo = (g) => {
        setExpanded((prev) => {
            const n = new Set(prev);
            if (n.has(g)) n.delete(g); else n.add(g);
            return n;
        });
    };

    // ---------- Master modal ----------
    const openMaster = (unidad) => {
        const m = masterDe(unidad.unidad_id);
        setMasterUnidad(unidad);
        setMasterForm(emptyMaster);
        setShowMasterForm(!m); // si no hay master, abrir directo el form
    };
    const submitMaster = async () => {
        if (!masterForm.nombre.trim() || !masterForm.apellido.trim() || !masterForm.email.trim()) {
            return toast.error("Completá nombre, apellido y email");
        }
        if (busy) return;
        const existente = masterDe(masterUnidad.unidad_id);
        const nuevoEmail = masterForm.email.trim().toLowerCase();
        // Si es el mismo email, hacer PATCH (rename)
        if (existente && existente.email === nuevoEmail) {
            setBusy(true);
            try {
                await api.patch(`/usuarios-edificio/${existente.usuario_edif_id}`, {
                    nombre: `${masterForm.nombre.trim()} ${masterForm.apellido.trim()}`.trim(),
                    whatsapp: masterForm.whatsapp.trim().replace(/\s+/g, " "),
                });
                setMasterForm(emptyMaster);
                setShowMasterForm(false);
                await load();
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setBusy(false); }
            return;
        }
        // Si hay master con otro email, lo eliminamos primero (replacement atómico desde UI)
        setBusy(true);
        try {
            if (existente) {
                await api.delete(`/usuarios-edificio/${existente.usuario_edif_id}`);
            }
            await api.post(`/usuarios-edificio/unidad/${masterUnidad.unidad_id}/pendiente`, {
                nombre: masterForm.nombre.trim(),
                apellido: masterForm.apellido.trim(),
                email: nuevoEmail,
                whatsapp: masterForm.whatsapp.trim().replace(/\s+/g, " "),
                es_master: true,
            });
            setMasterForm(emptyMaster);
            setShowMasterForm(false);
            await load();
        } catch (e) { toast.error(formatApiError(e)); await load(); }
        finally { setBusy(false); }
    };
    const eliminarMaster = async (m) => {
        if (!window.confirm(`¿Querés quitar este responsable de la unidad?\n\n${m.nombre} (${m.email})`)) return;
        setBusy(true);
        try {
            await api.delete(`/usuarios-edificio/${m.usuario_edif_id}`);
        } catch (e) {
            // Si ya no existe (404), tratamos como ok — la data estaba desincronizada.
            if (e?.response?.status !== 404) {
                toast.error(formatApiError(e));
                setBusy(false);
                return;
            }
        }
        setMasterUnidad(null);
        setShowMasterForm(false);
        await load();
        setBusy(false);
    };

    const masterActual = masterUnidad ? masterDe(masterUnidad.unidad_id) : null;
    const autosLista = autosUnidad ? autoDe(autosUnidad.unidad_id) : [];

    const resetearPassword = async (u) => {
        if (busy) return;
        setBusy(true);
        try {
            await api.post(`/usuarios-edificio/${u.usuario_edif_id}/reset`);
            await load();
            setResetFor(u);
            setResetCopied(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const resetMensaje = (u) => {
        const nombre = (u?.nombre || "").split(" ")[0] || "";
        return `Hola ${nombre}, necesitás resetear tu contraseña de la app Foresta. Entrá a este link y seguí el proceso normal: ${ACTIVATION_URL}`;
    };

    return (
        <div>
            <PageHeader
                title="Unidades & Responsables"
                subtitle={isRecepOnly
                    ? "Asigná responsables y consultá los autorizados de cada unidad."
                    : "Gestión de unidades y responsable del edificio. Los autorizados los carga cada responsable desde su perfil."}
            />

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar unidad, responsable, autorizado o email…"
                        className="pl-8 h-9"
                        data-testid="uni-search"
                    />
                </div>
                <Select value={filtroGrupo} onValueChange={setFiltroGrupo}>
                    <SelectTrigger className="w-full sm:w-44 h-9" data-testid="uni-filter">
                        <SelectValue placeholder="Grupo" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="todos">Todos los grupos</SelectItem>
                        {ORDEN_GRUPOS.map((g) => (
                            <SelectItem key={g} value={g}>{labelGrupo(g)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button onClick={newUni} data-testid="uni-new" className={`h-9 ${isRecepOnly ? "hidden" : ""}`}>
                    <Plus className="h-4 w-4 mr-1" /> Nueva unidad
                </Button>
                <Button
                    variant="outline"
                    className="h-9"
                    data-testid="copy-activacion-link"
                    onClick={async () => {
                        try {
                            await navigator.clipboard.writeText(ACTIVATION_MESSAGE_MASTER);
                            setCopiado(true);
                            setTimeout(() => setCopiado(false), 2000);
                        } catch {
                            toast.error("No se pudo copiar el mensaje");
                        }
                    }}
                >
                    {copiado ? <><Check className="h-3.5 w-3.5 mr-1.5" /> Copiado</> : <><Copy className="h-3.5 w-3.5 mr-1.5" /> Copiar mensaje de activación</>}
                </Button>
            </div>

            {gruposVisibles.length === 0 ? (
                <EmptyState title="Sin resultados" description="No hay unidades que coincidan con la búsqueda." />
            ) : (
                <div className="space-y-3">
                    {gruposVisibles.map(({ grupo, items }) => {
                        const open = expanded.has(grupo);
                        return (
                            <Card key={grupo} className="overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => toggleGrupo(grupo)}
                                    data-testid={`grupo-${grupo}`}
                                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-primary-soft/60 transition-colors text-left"
                                >
                                    <div className="flex items-center gap-2">
                                        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                        <p className="font-heading font-bold text-sm">{labelGrupo(grupo)}</p>
                                        <span className="text-xs text-muted-foreground">· {items.length} unidad{items.length === 1 ? "" : "es"}</span>
                                    </div>
                                </button>
                                {open && (
                                    <div className="border-t border-border">
                                        {/* Tabla desktop */}
                                        <div className="hidden md:block w-full">
                                            <table className="w-full table-fixed text-[13px]">
                                                <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                                                    <tr>
                                                        <th className="text-left font-semibold px-3 py-2 w-[110px]">Unidad</th>
                                                        <th className="text-center font-semibold px-2 py-2 w-[70px]">Piso</th>
                                                        <th className="text-right font-semibold px-2 py-2 w-[70px]">Cap.</th>
                                                        <th className="text-right font-semibold px-2 py-2 w-[80px]">Cupo</th>
                                                        <th className="text-left font-semibold px-3 py-2">Responsable</th>
                                                        <th className="text-left font-semibold px-3 py-2 w-[150px]">Autorizados</th>
                                                        <th className="px-2 py-2 w-[90px]"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {items.map((u) => {
                                                        const regla = matchRule(u.capacidad);
                                                        const master = masterDe(u.unidad_id);
                                                        const autos = autoDe(u.unidad_id);
                                                        const activos = autos.filter((x) => !x.pendiente_activacion).length;
                                                        const pend = autos.filter((x) => x.pendiente_activacion).length;
                                                        return (
                                                            <tr key={u.unidad_id} className="border-t border-border hover:bg-primary-soft/30">
                                                                <td className="px-3 py-1.5 font-medium whitespace-nowrap" data-testid={`uni-row-${u.etiqueta}`}>{u.etiqueta}</td>
                                                                <td className="px-2 py-1.5 text-center text-muted-foreground font-mono">{labelGrupoCorto(u.piso)}</td>
                                                                <td className="px-2 py-1.5 text-right font-mono">
                                                                    {u.capacidad ?? <span className="text-warning text-xs">—</span>}
                                                                </td>
                                                                <td className="px-2 py-1.5 text-right font-mono whitespace-nowrap">
                                                                    {regla ? (
                                                                        <span title={`Regla ${regla.capacidad_desde}–${regla.capacidad_hasta}`}>
                                                                            {regla.horas_semanales} h
                                                                        </span>
                                                                    ) : <span className="text-warning text-xs">—</span>}
                                                                </td>
                                                                <td className="px-3 py-1.5 min-w-0">
                                                                    {master ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openMaster(u)}
                                                                            data-testid={`master-${u.etiqueta}`}
                                                                            className="flex items-center gap-2 text-left hover:text-primary transition-colors w-full min-w-0"
                                                                        >
                                                                            <Crown className="h-3.5 w-3.5 text-accent shrink-0" />
                                                                            <span className="min-w-0 flex-1">
                                                                                <span className="block truncate font-medium leading-tight">{master.nombre}</span>
                                                                                <span className="block truncate text-[11px] text-muted-foreground font-mono leading-tight">{master.email}</span>
                                                                                {master.whatsapp && (
                                                                                    <span className="block truncate text-[10px] text-muted-foreground/80 leading-tight">
                                                                                        WhatsApp: <span className="font-mono">{master.whatsapp}</span>
                                                                                    </span>
                                                                                )}
                                                                            </span>
                                                                            {master.pendiente_activacion && (
                                                                                <Badge variant="secondary" className="text-[9px] px-1 py-0 shrink-0">Pend.</Badge>
                                                                            )}
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openMaster(u)}
                                                                            data-testid={`master-${u.etiqueta}`}
                                                                            className="inline-flex items-center gap-1 text-xs text-accent hover:text-primary hover:underline transition-colors whitespace-nowrap"
                                                                        >
                                                                            <UserPlus className="h-3 w-3" /> Asignar responsable
                                                                        </button>
                                                                    )}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs">
                                                                    {u.piso === "Coworking" ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setAutosUnidad(u)}
                                                                            data-testid={`autos-${u.etiqueta}`}
                                                                            className="text-muted-foreground/80 italic hover:text-primary transition-colors whitespace-nowrap"
                                                                            title="Coworking no permite autorizados"
                                                                        >
                                                                            —
                                                                        </button>
                                                                    ) : autos.length === 0 ? (
                                                                        <span className="text-muted-foreground/60">0</span>
                                                                    ) : (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setAutosUnidad(u)}
                                                                            data-testid={`autos-${u.etiqueta}`}
                                                                            className="hover:text-primary hover:underline transition-colors whitespace-nowrap"
                                                                        >
                                                                            <span className="font-semibold">{activos}</span> activo{activos === 1 ? "" : "s"}
                                                                            {pend > 0 && <span className="text-muted-foreground"> · {pend} pend.</span>}
                                                                        </button>
                                                                    )}
                                                                </td>
                                                                <td className="px-2 py-1.5 text-right">
                                                                    {!isRecepOnly && (
                                                                        <div className="inline-flex gap-0.5">
                                                                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => editUni(u)} data-testid={`uni-edit-${u.etiqueta}`}>
                                                                                <Pencil className="h-3.5 w-3.5" />
                                                                            </Button>
                                                                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => delUni(u)} data-testid={`uni-del-${u.etiqueta}`}>
                                                                                <Trash2 className="h-3.5 w-3.5" />
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        {/* Lista mobile */}
                                        <div className="md:hidden divide-y divide-border">
                                            {items.map((u) => {
                                                const regla = matchRule(u.capacidad);
                                                const master = masterDe(u.unidad_id);
                                                const autos = autoDe(u.unidad_id);
                                                return (
                                                    <div key={u.unidad_id} className="px-4 py-2.5">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <p className="font-semibold text-sm">{u.etiqueta}</p>
                                                            {!isRecepOnly && (
                                                                <div className="flex gap-0.5">
                                                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => editUni(u)}><Pencil className="h-3.5 w-3.5" /></Button>
                                                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => delUni(u)}><Trash2 className="h-3.5 w-3.5" /></Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <p className="text-[11px] text-muted-foreground font-mono mt-0.5">
                                                            Piso {labelGrupoCorto(u.piso)} · Cap {u.capacidad ?? "—"}{regla ? ` · ${regla.horas_semanales}h` : ""}
                                                        </p>
                                                        <div className="flex items-center justify-between gap-2 mt-1">
                                                            <button
                                                                type="button"
                                                                onClick={() => openMaster(u)}
                                                                className="text-[12px] hover:text-primary inline-flex items-center gap-1"
                                                            >
                                                                {master
                                                                    ? (<><Crown className="h-3 w-3 text-accent" />{master.nombre}</>)
                                                                    : (<span className="text-accent"><UserPlus className="inline h-3 w-3 mr-0.5" />Asignar responsable</span>)}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => (u.piso === "Coworking" || autos.length > 0) && setAutosUnidad(u)}
                                                                disabled={u.piso !== "Coworking" && autos.length === 0}
                                                                className="text-[12px] text-muted-foreground"
                                                            >
                                                                {u.piso === "Coworking" ? "—" : `${autos.length} auto${autos.length === 1 ? "" : "s"}`}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Modal editar unidad */}
            <Dialog open={uniOpen} onOpenChange={setUniOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{uniEdit ? "Editar unidad" : "Nueva unidad"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-2">
                            <Label>Etiqueta</Label>
                            <Input data-testid="uni-form-etiqueta" value={uniForm.etiqueta} onChange={(e) => setUniForm({ ...uniForm, etiqueta: e.target.value })} placeholder="Of. 101" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Grupo / Piso</Label>
                                <Select value={uniForm.piso} onValueChange={(v) => setUniForm({ ...uniForm, piso: v })}>
                                    <SelectTrigger data-testid="uni-form-piso"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent>
                                        {ORDEN_GRUPOS.map((g) => <SelectItem key={g} value={g}>{labelGrupo(g)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Capacidad (personas)</Label>
                                <Input type="number" min="1" data-testid="uni-form-capacidad" value={uniForm.capacidad} onChange={(e) => setUniForm({ ...uniForm, capacidad: e.target.value })} placeholder="8" />
                            </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                            La capacidad define qué regla de uso semanal de salas aplica.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setUniOpen(false)}>Cancelar</Button>
                        <Button onClick={submitUni} data-testid="uni-submit">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal Master */}
            <Dialog open={!!masterUnidad} onOpenChange={(o) => { if (!o) { setMasterUnidad(null); setShowMasterForm(false); } }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Responsable de {masterUnidad?.etiqueta}</DialogTitle>
                    </DialogHeader>
                    {masterUnidad && (
                        <div className="space-y-4">
                            {masterActual && !showMasterForm && (
                                <div className="flex items-center justify-between bg-primary-soft/50 border border-border rounded-md px-3 py-2.5">
                                    <div className="min-w-0 flex items-center gap-2">
                                        <Crown className="h-4 w-4 text-accent shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold truncate">{masterActual.nombre}</p>
                                            <p className="text-xs text-muted-foreground truncate font-mono">{masterActual.email}</p>
                                            {masterActual.whatsapp && (
                                                <p className="text-[11px] text-muted-foreground/80 truncate">
                                                    WhatsApp: <span className="font-mono">{masterActual.whatsapp}</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {masterActual.pendiente_activacion && (
                                            <Badge variant="secondary" className="text-[10px]">Pendiente</Badge>
                                        )}
                                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => eliminarMaster(masterActual)} data-testid="master-del">
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {masterActual?.pendiente_activacion && !showMasterForm && (
                                <p className="text-[11px] text-muted-foreground leading-snug bg-muted/30 border border-border rounded-md px-3 py-2">
                                    Este responsable está pendiente de activación. Copiá el link de activación desde el botón de arriba y mandáselo por WhatsApp.
                                </p>
                            )}

                            {masterActual && !showMasterForm && (
                                <div className="flex flex-col gap-2">
                                    <Button variant="outline" onClick={() => {
                                        setMasterForm({
                                            nombre: (masterActual.nombre || "").split(" ").slice(0, -1).join(" ") || masterActual.nombre || "",
                                            apellido: (masterActual.nombre || "").split(" ").slice(-1)[0] || "",
                                            email: masterActual.email || "",
                                            whatsapp: masterActual.whatsapp || "",
                                        });
                                        setShowMasterForm(true);
                                    }} className="w-full" data-testid="master-cambiar">
                                        Editar / Cambiar responsable
                                    </Button>
                                    {!masterActual.pendiente_activacion && (
                                        <Button
                                            variant="outline"
                                            onClick={() => resetearPassword(masterActual)}
                                            disabled={busy}
                                            className="w-full text-warning hover:text-warning border-warning/40 hover:bg-warning/10"
                                            data-testid={`reset-pw-${masterActual.usuario_edif_id}`}
                                        >
                                            <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Resetear contraseña
                                        </Button>
                                    )}
                                </div>
                            )}

                            {(!masterActual || showMasterForm) && (
                                <div className="space-y-3">
                                    {!masterActual && (
                                        <p className="text-[11px] text-muted-foreground">
                                            La unidad todavía no tiene responsable. Cargá los datos y después copiá el link de activación para mandárselo por WhatsApp.
                                        </p>
                                    )}
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1.5">
                                            <Label className="text-xs">Nombre</Label>
                                            <Input value={masterForm.nombre} onChange={(e) => setMasterForm({ ...masterForm, nombre: e.target.value })} data-testid="master-nombre" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label className="text-xs">Apellido</Label>
                                            <Input value={masterForm.apellido} onChange={(e) => setMasterForm({ ...masterForm, apellido: e.target.value })} data-testid="master-apellido" />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Email</Label>
                                        <Input type="email" value={masterForm.email} onChange={(e) => setMasterForm({ ...masterForm, email: e.target.value })} data-testid="master-email" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">WhatsApp</Label>
                                        <Input
                                            value={masterForm.whatsapp}
                                            onChange={(e) => setMasterForm({ ...masterForm, whatsapp: e.target.value })}
                                            data-testid="master-whatsapp"
                                            placeholder="Ej: 261 555 1234"
                                        />
                                    </div>
                                    <Button onClick={submitMaster} disabled={busy || !masterFormValido} className="w-full" data-testid="master-submit">
                                        <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Asignar responsable
                                    </Button>
                                    {!masterFormValido && (
                                        <p className="text-[11px] text-muted-foreground text-center">
                                            {!masterForm.nombre.trim() ? "Completá nombre." :
                                                !masterForm.apellido.trim() ? "Completá apellido." :
                                                    "Completá un email válido."}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setMasterUnidad(null); setShowMasterForm(false); }}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal Autorizados (solo lectura) */}
            <Dialog open={!!autosUnidad} onOpenChange={(o) => !o && setAutosUnidad(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Autorizados de {autosUnidad?.etiqueta}</DialogTitle>
                    </DialogHeader>
                    {autosUnidad?.piso === "Coworking" ? (
                        <p className="text-[12px] text-muted-foreground bg-muted/40 border border-border rounded-md px-3 py-2">
                            Coworking no permite autorizados. Solo el responsable tiene acceso a este puesto.
                        </p>
                    ) : (
                        <p className="text-[11px] text-muted-foreground -mt-2 mb-1">
                            Los autorizados los carga el responsable desde su perfil. Esta es una vista de consulta.
                        </p>
                    )}
                    {autosLista.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-6 text-center">
                            La unidad no tiene autorizados cargados.
                        </p>
                    ) : (
                        <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                            {autosLista.map((a) => (
                                <div key={a.usuario_edif_id} className="flex items-center justify-between border border-border rounded-md px-3 py-2 gap-2">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium truncate">{a.nombre}</p>
                                        <p className="text-[11px] text-muted-foreground font-mono truncate">{a.email}</p>
                                        {a.whatsapp && (
                                            <p className="text-[11px] text-muted-foreground/80 truncate">
                                                WhatsApp: <span className="font-mono">{a.whatsapp}</span>
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <Badge
                                            variant="secondary"
                                            className={`text-[10px] ${a.pendiente_activacion ? "bg-warning/15 text-warning" : "bg-success/15 text-success"}`}
                                        >
                                            {a.pendiente_activacion ? "Pendiente" : "Activo"}
                                        </Badge>
                                        {!a.pendiente_activacion && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => resetearPassword(a)}
                                                disabled={busy}
                                                className="h-7 px-2 text-warning hover:text-warning hover:bg-warning/10 text-[11px]"
                                                data-testid={`reset-pw-${a.usuario_edif_id}`}
                                                title="Resetear contraseña"
                                            >
                                                <KeyRound className="h-3 w-3 mr-1" /> Resetear
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAutosUnidad(null)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Modal Reset contraseña */}
            <Dialog open={!!resetFor} onOpenChange={(o) => { if (!o) { setResetFor(null); setResetCopied(false); } }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Contraseña reseteada</DialogTitle>
                    </DialogHeader>
                    {resetFor && (
                        <div className="space-y-3">
                            <div className="rounded-md bg-warning/10 border border-warning/30 px-3 py-2 text-[12px] text-foreground">
                                <p className="font-medium">{resetFor.nombre}</p>
                                <p className="text-muted-foreground font-mono text-[11px]">{resetFor.email}</p>
                                <p className="mt-1 text-[11px] text-muted-foreground">
                                    Volvió a quedar como pendiente. Mandale el mensaje por WhatsApp para que cree una contraseña nueva.
                                </p>
                            </div>
                            <div className="rounded-md bg-background border border-border p-2 max-h-32 overflow-auto">
                                <pre className="text-[11px] leading-relaxed text-muted-foreground whitespace-pre-wrap font-sans" data-testid="reset-msg-preview">{resetMensaje(resetFor)}</pre>
                            </div>
                            <Button
                                variant="outline"
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(resetMensaje(resetFor));
                                        setResetCopied(true);
                                        setTimeout(() => setResetCopied(false), 2500);
                                    } catch {
                                        toast.error("No se pudo copiar el mensaje");
                                    }
                                }}
                                className="w-full"
                                data-testid="reset-copy-msg"
                            >
                                {resetCopied ? <><Check className="h-3.5 w-3.5 mr-1.5" /> Copiado</> : <><Copy className="h-3.5 w-3.5 mr-1.5" /> Copiar mensaje</>}
                            </Button>
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={() => { setResetFor(null); setResetCopied(false); }}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
